/**
 * Modal.js
 * Reusable React Modal component with portal rendering
 *
 * Props:
 * - isOpen: Boolean indicating if modal is visible
 * - onClose: Function to call when modal should close
 * - title: Modal title (optional)
 * - children: Modal content (required)
 * - size: Modal size - 'small', 'medium', 'large' (default: 'medium')
 * - showBackdrop: Whether to show semi-transparent backdrop (default: true)
 * - onBackdropClick: Function to call when backdrop is clicked (default: onClose)
 * - footer: React node for footer content (optional)
 * - className: Extra class names on the dialog (optional)
 *
 * Usage:
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Configure Options"
 *   size="medium"
 * >
 *   <p>Modal content goes here</p>
 * </Modal>
 */

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Modal.css';
import ModalCloseButton from './ModalCloseButton';

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  showBackdrop = true,
  onBackdropClick,
  footer,
  className = '',
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = onBackdropClick || onClose;
  const isCompactScopeModal = className.split(/\s+/).includes('profile-scope-modal');

  const modal = (
    <div className={`modal-overlay ${showBackdrop ? 'modal-with-backdrop' : ''}`} onClick={handleBackdropClick}>
      <div
        className={`modal-dialog modal-${size}${className ? ` ${className}` : ''}`}
        style={isCompactScopeModal ? { width: 'min(360px, 90vw)' } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div
            className="modal-header"
            style={isCompactScopeModal ? { padding: '0.75rem 1rem' } : undefined}
          >
            <h2
              className="modal-title"
              style={isCompactScopeModal ? { fontSize: '1.05rem' } : undefined}
            >
              {title}
            </h2>
            <ModalCloseButton onClick={onClose} title="Close" />
          </div>
        )}

        {/* Body */}
        <div
          className="modal-body"
          style={isCompactScopeModal
            ? { padding: '0.5rem 1rem 0.85rem', flex: '0 0 auto' }
            : undefined}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Render modal to portal (document.body or custom container)
  return ReactDOM.createPortal(modal, document.body);
};

export default Modal;
